import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import {
  useUpdateUserDataMutation,
  useUserInfoQuery,
} from '@/store/api/Users/Users.query'
import type { profileType } from '@/utils/schemas'
import { profileSchema } from '@/utils/schemas'

const LANGUAGES = [
  { code: 'en', labelKey: 'dashboard.account.english' },
  { code: 'es', labelKey: 'dashboard.account.spanish' },
] as const

export const Profile = () => {
  const {
    t,
    i18n: { changeLanguage, language },
  } = useTranslation()

  const { data, isLoading: isLoadingUserData } = useUserInfoQuery()
  const [updateUserData, { isLoading: isLoadingUpdateData }] =
    useUpdateUserDataMutation()

  const [selectedLanguage, setSelectedLanguage] = useState<string>(language)

  const form = useForm<profileType>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: data?.firstname,
      lastName: data?.lastname,
      phoneNumber: data?.phoneNumber,
      mail: data?.email,
    },
    mode: 'onChange',
  })
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = form

  const handleUpdateUserData = async (formData: profileType) => {
    try {
      await updateUserData({
        phoneNumber: formData.phoneNumber ?? '',
        firstName: formData.firstName,
        lastName: formData.lastName,
        language: selectedLanguage,
      }).unwrap()
      changeLanguage(selectedLanguage)
      toast.success(t('feedback.toast.changesSaved'))
    } catch (error) {
      toast.error(t('feedback.toast.errorSavingChanges'))
      catchWithSentry(error)
    }
  }

  const initials = useMemo(() => {
    if (!data?.firstname || !data?.lastname) return ''
    const first = data.firstname[0]?.toUpperCase() ?? ''
    const last = data.lastname[0]?.toUpperCase() ?? ''
    return `${first}${last}`
  }, [data?.firstname, data?.lastname])

  if (isLoadingUserData) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LottieSpinner width={120} height={120} />
      </div>
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(handleUpdateUserData)}
        className="flex flex-1 min-h-0 flex-col"
      >
        <div className="flex flex-1 min-h-0 flex-col gap-8 overflow-y-auto">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-medium text-neutralColor">
              {t('dashboard.account.account')}
            </h2>
            <p className="text-sm text-neutralColor60">
              {t('dashboard.account.accountSubtitle')}
            </p>
          </div>

          <Avatar className="h-20 w-20">
            <AvatarFallback className="bg-baseColor text-xl text-neutralColor">
              {initials}
            </AvatarFallback>
          </Avatar>

          <section className="flex flex-col gap-3">
            <h3 className="text-xl font-medium text-neutralColor">
              {t('dashboard.account.personalDetails')}
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('dashboard.account.firstName')}
                        className="h-11 rounded-lg"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('dashboard.account.lastName')}
                        className="h-11 rounded-lg"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="text-xl font-medium text-neutralColor">
              {t('dashboard.account.contactInfo')}
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                disabled
                control={control}
                defaultValue={data?.email}
                name="mail"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('dashboard.account.email')}
                        className="h-11 rounded-lg"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('dashboard.account.phoneNumber')}
                        className="h-11 rounded-lg"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="text-xl font-medium text-neutralColor">
              {t('dashboard.account.languageTitle')}
            </h3>
            <ToggleGroup
              type="single"
              value={selectedLanguage}
              onValueChange={(value) => {
                if (!value) return
                setSelectedLanguage(value)
              }}
            >
              {LANGUAGES.map(({ code, labelKey }) => (
                <ToggleGroupItem key={code} value={code}>
                  {t(labelKey)}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </section>
        </div>

        <div className="flex shrink-0 justify-end pt-6">
          <Button
            type="submit"
            disabled={!isDirty && selectedLanguage === language}
            isLoading={isLoadingUpdateData}
            className="self-end"
          >
            {t('dashboard.account.save')}
          </Button>
        </div>
      </form>
    </Form>
  )
}
